import { ref, reactive, onBeforeUnmount, computed } from '@vue/composition-api'
import gql from 'graphql-tag'
import toGraphql from '@iez/lib/to-graphql/'
import { getClient } from '..'
import handleValueOrFunction from '@iez/lib/handle-value-or-function/'
import waitFor from '@iez/lib/wait-for/'

const normalizeRoot = ({ root }) =>
  root.includes(`_aggregate`) ? root.substr(0, root.indexOf(`_aggregate`)) : root

const _get = ({ root, fields, name, random }) =>
  gql`
    ${name} (
      ${random ? `$args: ${root}_random_args!,` : ``}
      $limit: Int,
      $order_by: [${normalizeRoot({ root })}_order_by!],
      $where: ${normalizeRoot({ root })}_bool_exp
    ) {
      ${random ? `${root}_random` : root} (
        ${random ? `args: $args` : ``}
        limit: $limit
        order_by: $order_by
        where: $where
      ) {
        ${toGraphql(fields)}
      }
    }
  `

const getQuery = ({ root, fields, random }) =>
  _get({ root, fields, name: 'query', random })
// const getSubscription = ({ root, fields }) => get({ root, fields, name: 'subscription' })

/**
 * subscription lives in one route,
 * so unsubscribe is called on unmounted.
 */
export default ({
  root,
  request,
  fields,
  realtime,
  one=false,
  isCalledFromComponent=true,
  waitFor: waitForDependencies,
  fetchPolicy,
  keepSameIdOnRealtime,
  random
}) => {
  const client = getClient()

  const query = getQuery({ root, fields, random })

  const result = ref(
    undefined
  )

  const subscriptionInterval = ref(null)

  const toClean = []
  const clean = () => {
    unsubscribe()
    while (toClean.length) {
      toClean.shift()()
    }
  }

  const unsubscribe = () => {
    if (subscriptionInterval.value) {
      clearInterval(subscriptionInterval.value)
      subscriptionInterval.value = null
    }
  }

  const process = variables => {
    let isRealtimeHandled = false
    const updateResult = payload => {
      const { data } = payload
      if (!data) {
        console.error('LOOK HERE SOMETHING IS HAPPENING PLEASE CHECK AND DOCUMENT')
        result.value = null
        return
      }
      const value = Object.values(data)[0]
      const resultReceived = Array.isArray(value)
        ? one
          ? value.length
            ? { ...value[0] }
            : null
          : [ ...value ]
        : { ...value }
      if (!isCalledFromComponent && resultReceived) {
        resultReceived.$clean = clean
      }
      result.value = resultReceived

      /**
       * We handle here realime based on fetched id
       */
      if (!isRealtimeHandled) {
        isRealtimeHandled = true
        if (keepSameIdOnRealtime) {
          handleRealtime({
            variables: {
              where: {
                id: {
                  _in: Array.isArray(result)
                    ? result.map(({ id }) => id)
                    : [result.id]
                }
              }
            }
          })
        } else {
          handleRealtime({
            variables
          })
        }
      }
    }

    client.watchQuery({
      query,
      variables: {
        ...variables,
        ...one
          ? {
            limit: variables.limit ?? 1
          } : undefined
      },
      fetchPolicy: fetchPolicy || `cache-and-network`
    }).subscribe(payload => {
      updateResult(payload)
    })

    const handleRealtime = ({ variables }) => {
      if (realtime) {
        /**
         * Here we would like to refetch base on id
         * fetched from first query
         */
        const doQuery = () => {
          client.query({
            query,
            variables: {
              ...variables,
              ...one
                ? {
                  limit: variables.limit ?? 1
                } : undefined
            },
            fetchPolicy: `network-only`
          })
        }
        const subscribe = () => {
          unsubscribe()
          subscriptionInterval.value = setInterval(
            doQuery
          , 2000)
          /* state.subscription = client.subscribe({
            query: getSubscription({ root, fields }),
            variables
          }).subscribe(updateResult) */
        }
        toClean.push(
          handleValueOrFunction({
            valueOrFunction: realtime,
            callback: realtime => {
              if (realtime) {
                subscribe()
              } else {
                unsubscribe()
              }
            }
          })
        )
      }
    }
  }

  waitFor({
    dependencies: waitForDependencies
  }).then(() => {
    toClean.push(
      handleValueOrFunction({
        valueOrFunction: request,
        callback: request => {
          // result.value = null
          process(request)
        }
      })
    )
  })

  if (isCalledFromComponent) {
    onBeforeUnmount(() => {
      clean()
    })
  }

  return result
}
